import React, { useState, useEffect } from 'react';
import { ParkingSpaceWithContract, ParkingSpaceFormData, PARKING_STATUS_LABELS } from '../../../types/parkingSpace';

interface ParkingSpaceFormProps {
  space?: ParkingSpaceWithContract;
  onSubmit: (formData: ParkingSpaceFormData) => Promise<boolean>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const ParkingSpaceForm: React.FC<ParkingSpaceFormProps> = ({ space, onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState<ParkingSpaceFormData>({
    spaceNumber: '',
    status: 'vacant',
    userName: '',
    userPhoneNumber: '',
    userOrganization: '',
    startDate: '',
    endDate: '',
    vehicleInfo: '',
    notes: ''
  });

  useEffect(() => {
    if (space) {
      setFormData({
        spaceNumber: space.spaceNumber,
        status: space.status,
        userName: space.currentContract?.userInfo?.name || '',
        userPhoneNumber: space.currentContract?.userInfo?.phoneNumber || '',
        userOrganization: space.currentContract?.userInfo?.organization || '',
        startDate: space.currentContract?.usagePeriod?.startDate ? 
          space.currentContract.usagePeriod.startDate.toISOString().split('T')[0] : '',
        endDate: space.currentContract?.usagePeriod?.endDate ? 
          space.currentContract.usagePeriod.endDate.toISOString().split('T')[0] : '',
        vehicleInfo: space.currentContract?.vehicleInfo || '',
        notes: space.currentContract?.notes || ''
      });
    }
  }, [space]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await onSubmit(formData);
    if (success) {
      if (!space) {
        // 새 주차면 추가인 경우 폼 초기화
        setFormData({
          spaceNumber: '',
          status: 'vacant',
          userName: '',
          userPhoneNumber: '',
          userOrganization: '',
          startDate: '',
          endDate: '',
          vehicleInfo: '',
          notes: ''
        });
      }
    }
  };

  const handleInputChange = (field: keyof ParkingSpaceFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isOccupied = formData.status === 'occupied';
  const isContracting = formData.status === 'contracting';
  const showUserInfo = isOccupied || isContracting;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 주차면번호 */}
        <div>
          <label htmlFor="spaceNumber" className="block text-sm font-medium text-gray-700 mb-2">
            주차면번호 *
          </label>
          <input
            type="text"
            id="spaceNumber"
            required
            value={formData.spaceNumber}
            onChange={(e) => handleInputChange('spaceNumber', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="예: A01, B02"
          />
        </div>

        {/* 상태 */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
            상태 *
          </label>
          <select
            id="status"
            required
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Object.entries(PARKING_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 사용중이거나 계약진행인 경우에만 표시되는 필드들 */}
      {showUserInfo && (
        <>
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {isOccupied ? '이용자 정보' : '예정 이용자 정보'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-2">
                  성명 *
                </label>
                <input
                  type="text"
                  id="userName"
                  required={showUserInfo}
                  value={formData.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="userPhoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  연락처
                </label>
                <input
                  type="tel"
                  id="userPhoneNumber"
                  value={formData.userPhoneNumber}
                  onChange={(e) => handleInputChange('userPhoneNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="userOrganization" className="block text-sm font-medium text-gray-700 mb-2">
                  소속
                </label>
                <input
                  type="text"
                  id="userOrganization"
                  value={formData.userOrganization}
                  onChange={(e) => handleInputChange('userOrganization', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* 관리자 추가 정보 */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">추가 정보</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="vehicleInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  차량정보
                </label>
                <input
                  type="text"
                  id="vehicleInfo"
                  value={formData.vehicleInfo}
                  onChange={(e) => handleInputChange('vehicleInfo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="차량번호, 차종 등"
                />
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  비고
                </label>
                <input
                  type="text"
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="기타 특이사항"
                />
              </div>
            </div>
          </div>

          {/* 사용중인 경우에만 이용기간 입력 */}
          {isOccupied && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">이용기간</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                    이용 개시일 *
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    required={isOccupied}
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                    계약 종료일 *
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    required={isOccupied}
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 버튼 */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={isSubmitting}
        >
          취소
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? '저장 중...' : space ? '수정' : '추가'}
        </button>
      </div>
    </form>
  );
};

export default ParkingSpaceForm; 